import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Badge, type BadgeVariant } from "@/components/Badge";
import { Textarea } from "@/components/Textarea";
import { Button } from "@/components/Button";
import { Markdown } from "@/components/Markdown";
import { Accordion } from "@/components/Accordion";
import { Tooltip } from "@/components/Tooltip";
import { tasksApi, taskStatusesApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { date } from "@/utils/format";
import { ChangeTaskPriorityModal } from "@/pages/Tasks/ChangeTaskPriorityModal";
import { ChangeTaskStatusModal } from "@/pages/Tasks/ChangeTaskStatusModal";
import { ChangeTaskAssigneeModal } from "@/pages/Tasks/ChangeTaskAssigneeModal";
import { NewTaskModal } from "@/pages/Tasks/NewTaskModal";
import { Modal } from "@/components/Modal";
import { Collapse } from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import { getInitials } from "@/utils/format";
import { PageHeader } from "@/components/PageHeader";
import { Select, type SelectOption } from "@/components/Select";
import { TaskPhaseTimeline } from "@/pages/Tasks/TaskPhaseTimeline";

export interface TaskViewProps {
    type?: string;
    taskId: number | string;
    taskCode?: string;
    initialTask?: any;
    onEdit?: (task: any) => void;
    onTaskUpdate?: (task: any) => void;
}

const TASK_STATUS_VARIANT: Record<string, BadgeVariant> = {
    todo: "neutral",
    progress: "info",
    review: "warning",
    done: "success",
    cancelled: "danger",
};

import { PRIORITY_CONFIG } from "@/components/PrioritySelect";

const PRIORITY_BADGE_VARIANT: Record<number | string, BadgeVariant> = {
    1: "secondary",
    2: "info",
    3: "warning",
    4: "danger",
    "1": "secondary",
    "2": "info",
    "3": "warning",
    "4": "danger",
    low: "secondary",
    medium: "info",
    high: "warning",
    urgent: "danger",
};

const isComposerEmpty = (content: string): boolean => {
    const trimmed = content.trim();
    if (!trimmed) {
        return true;
    }
    if (trimmed.includes("<img")) {
        return false;
    }
    const textOnly = trimmed
        .replace(/<br\s*\/?>/gi, "")
        .replace(/<\/?p[^>]*>/gi, "")
        .replace(/<\/?div[^>]*>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/<[^>]+>/g, "")
        .trim();
    return textOnly.length === 0;
};

const isMessageEdited = (
    createdAt?: string | null,
    updatedAt?: string | null
): boolean => {
    if (!createdAt || !updatedAt) {
        return false;
    }

    const createdTime = new Date(createdAt).getTime();
    const updatedTime = new Date(updatedAt).getTime();
    if (isNaN(createdTime) || isNaN(updatedTime)) {
        return false;
    }

    return updatedTime - createdTime > 1000;
};

export const TaskView: React.FC<TaskViewProps> = ({
    type = null,
    taskId,
    taskCode,
    initialTask = null,
    onEdit,
    onTaskUpdate,
}) => {
    const { t } = useTranslation("global");
    const { showError, showSuccess } = useToast();
    const { user } = useAuth();
    const [task, setTask] = useState(initialTask);
    const [loading, setLoading] = useState(!initialTask);
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState("");
    const [commentType, setCommentType] = useState("general");
    const [commentFilter, setCommentFilter] = useState("general");
    const [sending, setSending] = useState(false);
    const [isWritingComment, setIsWritingComment] = useState(false);
    const [replyingToId, setReplyingToId] = useState<number | null>(null);
    const [threadReplyText, setThreadReplyText] = useState("");
    const [threadSending, setThreadSending] = useState(false);

    const commentTypes = ["general", "frontend", "backend", "database"];
    const commentTypeOptions: SelectOption<string>[] = commentTypes.map(
        (commentTypeOption) => ({
            value: commentTypeOption,
            label: t(`tasks.comments.types.${commentTypeOption}`),
        })
    );
    const [editingMessageId, setEditingMessageId] = useState<number | null>(
        null
    );
    const [editingContent, setEditingContent] = useState<string>("");
    const [editSaving, setEditSaving] = useState<boolean>(false);
    const [deleteTargetMessageId, setDeleteTargetMessageId] = useState<
        number | null
    >(null);
    const [isDeletingMessage, setIsDeletingMessage] = useState<boolean>(false);
    const [statusTarget, setStatusTarget] = useState(null);
    const [priorityTarget, setPriorityTarget] = useState(null);
    const [assigneeTarget, setAssigneeTarget] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const [copiedMsgId, setCopiedMsgId] = useState<number | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [descExpanded, setDescExpanded] = useState(false);

    const [errorState, setErrorState] = useState(false);

    useEffect(() => {
        if (!onEdit) {
            taskStatusesApi
                .list()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setStatuses(res.data);
                    }
                })
                .catch(console.error);
        }
    }, [onEdit]);

    useEffect(() => {
        if (taskId) {
            loadTask();
            loadMessages();
        }
    }, [taskId]);

    const loadTask = async () => {
        try {
            setLoading(true);
            setErrorState(false);
            const res = await tasksApi.get(taskId);
            if (res.result) {
                setTask(res.data);
            }
        } catch (error) {
            setErrorState(true);
            showError(t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    const loadMessages = async () => {
        try {
            const res = await tasksApi.getMessages(taskId);
            if (res.result) {
                const sorted = [...(res.data || [])].sort(
                    (a: any, b: any) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                );
                setMessages(sorted);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSendReply = async () => {
        if (isComposerEmpty(replyText) && pendingFiles.length === 0) {
            return;
        }
        setSending(true);
        try {
            const res = await tasksApi.sendMessage(taskId, {
                content: replyText,
                is_internal: false,
                comment_type: commentType,
            });
            if (res.result && res.data?.id && pendingFiles.length > 0) {
                for (const file of pendingFiles) {
                    await tasksApi.uploadAttachment(taskId, file, res.data.id);
                }
            }
            setReplyText("");
            setPendingFiles([]);
            setIsWritingComment(false);
            loadMessages();
        } catch (error) {
            showError(t("errors.default"));
        } finally {
            setSending(false);
        }
    };

    const handleSendThreadReply = async (parentId: number) => {
        if (isComposerEmpty(threadReplyText)) {
            return;
        }
        setThreadSending(true);
        try {
            await tasksApi.sendMessage(taskId, {
                content: threadReplyText,
                parent_id: parentId,
                is_internal: false,
                comment_type: commentType,
            });
            setThreadReplyText("");
            setReplyingToId(null);
            loadMessages();
        } catch (error) {
            showError(t("errors.default"));
        } finally {
            setThreadSending(false);
        }
    };

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            setPendingFiles((prev) => [...prev, ...(files as File[])]);
        }
    }, []);

    const handleTextareaChange = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        setReplyText(e.target.value);
    };

    const handleCopyContent = (htmlContent: string, msgId: number): void => {
        const div = document.createElement("div");
        div.innerHTML = htmlContent || "";
        const text = div.textContent || div.innerText || "";
        navigator.clipboard.writeText(text);
        setCopiedMsgId(msgId);
        setTimeout(() => {
            setCopiedMsgId(null);
        }, 2000);
    };

    const handleStartEdit = (msgId: number, currentContent: string): void => {
        setEditingMessageId(msgId);
        setEditingContent(currentContent || "");
    };

    const handleCancelEdit = (): void => {
        setEditingMessageId(null);
        setEditingContent("");
    };

    const handleSaveEdit = async (msgId: number): Promise<void> => {
        if (isComposerEmpty(editingContent)) {
            return;
        }

        setEditSaving(true);
        try {
            await tasksApi.updateMessage(taskId, msgId, {
                content: editingContent,
            });
            showSuccess(t("common.saved"));
            setEditingMessageId(null);
            setEditingContent("");
            loadMessages();
        } catch (error) {
            showError(t("errors.default"));
        } finally {
            setEditSaving(false);
        }
    };

    const handleConfirmDeleteMessage = async (): Promise<void> => {
        if (!deleteTargetMessageId) {
            return;
        }

        setIsDeletingMessage(true);
        try {
            await tasksApi.deleteMessage(taskId, deleteTargetMessageId);
            showSuccess(t("tasks.comments.delete_success"));
            setDeleteTargetMessageId(null);
            loadMessages();
        } catch (error) {
            showError(t("errors.default"));
        } finally {
            setIsDeletingMessage(false);
        }
    };

    const handleContentClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;

        const blockquote = target.closest("blockquote");
        if (blockquote) {
            const linkInside = blockquote.querySelector('a[href^="#msg-"]');
            if (linkInside) {
                e.preventDefault();
                const href = linkInside.getAttribute("href");
                if (href) {
                    const id = href.substring(1);
                    const el = document.getElementById(id);
                    if (el) {
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                        el.classList.remove("blink-target");
                        void el.offsetWidth;
                        el.classList.add("blink-target");
                    }
                }
                return;
            }
        }

        const link = target.closest("a");
        if (link) {
            const href = link.getAttribute("href");
            if (href && href.startsWith("#msg-")) {
                e.preventDefault();
                const id = href.substring(1);
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.classList.remove("blink-target");
                    void el.offsetWidth; // trigger reflow
                    el.classList.add("blink-target");
                }
            }
        }
    };

    const handleQuoteMessage = (msg: any, parentId?: number): void => {
        const authorName = msg.user?.name || t("tasks.comments.unknown_user");

        const doc = new DOMParser().parseFromString(
            msg.content || "",
            "text/html"
        );
        let plainContent = doc.body.textContent || "";
        if (plainContent.length > 150) {
            plainContent = plainContent.substring(0, 150) + "...";
        }

        const quoteHtml = `<blockquote><a href="#msg-${msg.id}"><strong>${authorName}:</strong></a><br/>${plainContent}</blockquote><p></p>`;

        setIsWritingComment(true);
        setReplyText((prev: string) => {
            if (prev) {
                return `${prev}<br/>${quoteHtml}`;
            }

            return quoteHtml;
        });

        setTimeout(() => {
            document
                .getElementById("main-composer-accordion")
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
            </div>
        );
    }

    if (errorState || !task) {
        return (
            <div className="flex flex-col justify-center items-center h-64 gap-4 text-slate-500">
                <Icon
                    value="alertCircle"
                    size={48}
                    className="text-slate-300"
                />
                <p>{t("tasks.not_found")}</p>
            </div>
        );
    }

    const priorityKey = task.priority ?? 2;
    const priorityCfg = PRIORITY_CONFIG[priorityKey] || PRIORITY_CONFIG[2];
    const statusTag = task.status?.tag || "";

    return (
        <div className="flex flex-col gap-6">
            {/* Page Header */}
            <PageHeader
                compact
                className="!mb-0"
                title={
                    <div className={type && type === "page" ? "flex" : ""}>
                        <div
                            className={
                                type && type === "page" ? "" : "text-center"
                            }
                        >
                            {(task.code || taskCode) + " - " + task.title}
                        </div>
                        {type && type === "page" && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (onEdit) {
                                        onEdit(task);
                                    } else {
                                        setIsEditModalOpen(true);
                                    }
                                }}
                                className="ml-2 text-slate-400 hover:text-primary transition-colors cursor-pointer shrink-0"
                                title={t("common.edit")}
                            >
                                <Icon
                                    value="pencil"
                                    size={20}
                                    className="mb-1"
                                />
                            </button>
                        )}
                    </div>
                }
                subtitle={
                    <div
                        className={type && type === "page" ? "" : "text-center"}
                    >
                        {date(task.created_at)}
                    </div>
                }
                action={
                    type && type === "page" ? (
                        <Button variant="primary" onClick={() => navigate(-1)}>
                            {t("tasks.back")}
                        </Button>
                    ) : null
                }
            />

            {/* Properties List */}
            <div className="box max-w-4xl mx-auto flex flex-col gap-3 rounded-xl p-4 text-sm border border-slate-200 dark:border-zinc-800 w-full">
                {/* Date */}
                {/*
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                    <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0">{t('tasks.table.date')}</span>
                    <div className="flex-1 flex sm:justify-end">
                        {date(task.created_at)}
                    </div>
                </div>                
                */}

                {/* Status */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                    <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0">
                        {t("tasks.table.status")}
                    </span>
                    <div className="flex-1 flex sm:justify-end">
                        <Badge
                            onClick={() => setStatusTarget(task)}
                            variant={
                                TASK_STATUS_VARIANT[statusTag] ?? "neutral"
                            }
                            value={
                                task.status
                                    ? t(`tasks.statuses.${statusTag}`)
                                    : "—"
                            }
                        />
                    </div>
                </div>

                {/* Priority */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                    <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0">
                        {t("tasks.table.priority")}
                    </span>
                    <div className="flex-1 flex sm:justify-end">
                        <Badge
                            onClick={() => setPriorityTarget(task)}
                            variant={
                                PRIORITY_BADGE_VARIANT[priorityCfg.tag] ??
                                "neutral"
                            }
                        >
                            <span className="flex items-center -space-x-0.5">
                                {Array.from({
                                    length: priorityCfg?.arrows || 2,
                                }).map((_, idx) => (
                                    <Icon
                                        key={idx}
                                        value="arrowUp"
                                        size={12}
                                        strokeWidth={3}
                                    />
                                ))}
                            </span>
                            <span className="">
                                {t(`priorities.${priorityCfg.tag}`)}
                            </span>
                        </Badge>
                    </div>
                </div>

                {/* Assignee */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                    <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0">
                        {t("tasks.table.assignee")}
                    </span>
                    <div className="flex-1 flex sm:justify-end">
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                setAssigneeTarget(task);
                            }}
                            className="cursor-pointer rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            title={t("tasks.modal_assignee.title")}
                        >
                            {task.assignee ? (
                                <Badge
                                    value={task.assignee.name}
                                    variant="neutral"
                                    icon="user"
                                    className="pointer-events-none"
                                />
                            ) : (
                                <Badge
                                    variant="neutral"
                                    className="pointer-events-none italic opacity-70"
                                >
                                    {t("tasks.table.unassigned")}
                                </Badge>
                            )}
                        </button>
                    </div>
                </div>

                {/* Development */}
                {task.development && (
                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                        <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0 sm:mt-0.5">
                            {t("tasks.table.development") || "Desarrollo"}
                        </span>
                        <div className="flex-1 flex sm:justify-end">
                            <a
                                href={`/developments/${task.development.id}`}
                                className="hover:opacity-80 transition-opacity"
                            >
                                <Badge
                                    variant="transparent"
                                    icon="folder"
                                    value={`${task.development.code} - ${task.development.title}`}
                                />
                            </a>
                        </div>
                    </div>
                )}

                {/* Related Tasks */}
                {(() => {
                    const allRelated = [
                        ...(task.relatedTasks || task.related_tasks || []),
                        ...(task.inverseRelatedTasks ||
                            task.inverse_related_tasks ||
                            []),
                    ].filter(
                        (v, i, a) => a.findIndex((t) => t.id === v.id) === i
                    );

                    if (allRelated.length === 0) return null;
                    return (
                        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                            <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0 sm:mt-0.5">
                                {t("tasks.new_modal.related_tasks") ||
                                    "Relacionadas"}
                            </span>
                            <div className="flex-1 flex flex-col sm:items-end gap-1.5">
                                {allRelated.map((rt: any) => (
                                    <a
                                        key={rt.id}
                                        href={`/tasks/${rt.id}`}
                                        className="hover:opacity-80 transition-opacity"
                                    >
                                        <Badge
                                            variant="neutral"
                                            icon="link"
                                            value={`${rt.code} - ${rt.title}`}
                                        />
                                    </a>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* Dependencies */}
                {task.dependencies && task.dependencies.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                        <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0 sm:mt-0.5">
                            {t("tasks.new_modal.dependencies") || "Depende de"}
                        </span>
                        <div className="flex-1 flex flex-col sm:items-end gap-1.5">
                            {task.dependencies.map((dep: any) => (
                                <a
                                    key={dep.id}
                                    href={`/tasks/${dep.id}`}
                                    className="hover:opacity-80 transition-opacity"
                                >
                                    <Badge
                                        variant="danger"
                                        icon="lock"
                                        value={`${dep.code} - ${dep.title}`}
                                    />
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Dependents */}
                {task.dependents && task.dependents.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 pb-2 last:border-0 last:pb-0">
                        <span className="font-semibold text-slate-500 dark:text-zinc-400 sm:w-1/3 shrink-0 sm:mt-0.5">
                            {t("tasks.dependents") || "Bloquea a"}
                        </span>
                        <div className="flex-1 flex flex-col sm:items-end gap-1.5">
                            {task.dependents.map((dep: any) => (
                                <a
                                    key={dep.id}
                                    href={`/tasks/${dep.id}`}
                                    className="hover:opacity-80 transition-opacity"
                                >
                                    <Badge
                                        variant="warning"
                                        icon="lock"
                                        value={`${dep.code} - ${dep.title}`}
                                    />
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Description */}
            <div className="box">
                {task.description ? (
                    <>
                        <div
                            style={{
                                maxHeight: descExpanded ? "10000px" : "300px",
                                overflow: "hidden",
                                transition:
                                    "max-height 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                            className="relative"
                        >
                            <Markdown>{task.description}</Markdown>
                            {!descExpanded && task.description.length > 300 && (
                                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white dark:from-zinc-900 to-transparent pointer-events-none" />
                            )}
                        </div>
                        {task.description.length > 300 && (
                            <div className="flex justify-center mt-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setDescExpanded((prev) => !prev)
                                    }
                                    className="btn-ghost"
                                >
                                    {descExpanded
                                        ? t("common.see_less")
                                        : t("common.see_more")}
                                    <Icon
                                        value={
                                            descExpanded
                                                ? "chevronUp"
                                                : "chevronDown"
                                        }
                                        size={14}
                                    />
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <span className="italic text-slate-400 text-sm">
                        {t("tasks.no_description")}
                    </span>
                )}
            </div>

            {/* Comments */}
            <div className="pt-6">
                <TaskPhaseTimeline
                    messages={messages}
                    activePhase={commentFilter as "all" | "general" | "frontend" | "backend" | "database"}
                    onSelectPhase={setCommentFilter}
                    onAdvance={() => {
                        const phases = ["general", "frontend", "backend", "database"];
                        const currentIndex = phases.indexOf(commentFilter);
                        const nextPhase = phases[currentIndex + 1];

                        if (nextPhase) {
                            setCommentFilter(nextPhase);
                        } else {
                            setCommentFilter("all");
                        }
                    }}
                />

                {/* Title */}
                <div className="flex items-center justify-center mb-4">
                    <h3 className="text-base font-bold text-slate-800 dark:text-zinc-100 flex items-center gap-2">
                        <Icon value="message" size={18} />
                        {t("tasks.comments.title")}
                    </h3>
                </div>

                {/* Composer toggle button */}
                {!isWritingComment && (
                    <div className="flex justify-center mb-4">
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setIsWritingComment(true)}
                        >
                            <Icon value="pencil" size={15} />
                            {t("tasks.comments.write")}
                        </button>
                    </div>
                )}

                {/* Composer (collapses smoothly) */}
                <Collapse
                    in={isWritingComment}
                    timeout={300}
                    unmountOnExit
                    className="overflow-visible-on-entered"
                >
                    <div className="mb-4" id="main-composer-accordion">
                        <div
                            className={`relative transition-all ${isDragOver ? "border-primary border-2 bg-primary/5 rounded-xl" : ""}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            {isDragOver && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-xl bg-primary/10 border-2 border-dashed border-primary/50">
                                    <div className="flex flex-col items-center gap-2 text-primary">
                                        <Icon value="upload" size={28} />
                                        <span className="text-sm font-medium">
                                            {t("tasks.comments.drop_files")}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="mb-3 rounded-lg bg-primary/5 px-3 py-2 text-xs text-slate-600 dark:bg-primary/10 dark:text-zinc-300">
                                {t("tasks.comments.phase_hint")}
                            </div>

                            <div className="mb-3 max-w-xs">
                                <Select
                                    value={commentType}
                                    onChange={(value) => setCommentType(String(value))}
                                    options={commentTypeOptions}
                                    label={t("tasks.comments.type")}
                                    ariaLabel={t("tasks.comments.type")}
                                    full
                                    size="sm"
                                />
                            </div>

                            <Textarea
                                ref={composerRef}
                                value={replyText}
                                onChange={handleTextareaChange}
                                placeholder={t("tasks.comments.placeholder")}
                                rows={3}
                                autoGrow
                                richText
                                bare
                                rounded
                            />

                            {/* Files */}
                            {pendingFiles.length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-3 pb-1">
                                    {pendingFiles.map((f: File, i: number) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-1 text-xs bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-lg px-2 py-1"
                                        >
                                            <Icon value="paperclip" size={12} />
                                            <span className="max-w-[120px] truncate">
                                                {f.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setPendingFiles((prev) =>
                                                        prev.filter(
                                                            (_, j) => j !== i
                                                        )
                                                    )
                                                }
                                            >
                                                <Icon
                                                    value="x"
                                                    size={12}
                                                    className="text-slate-400 hover:text-red-500"
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center justify-center gap-3 pt-3 pb-1">
                                <button
                                    type="button"
                                    className="btn-secondary px-5 py-1.5 text-sm font-semibold rounded-xl cursor-pointer"
                                    onClick={() => {
                                        setIsWritingComment(false);
                                        setReplyText("");
                                        setPendingFiles([]);
                                    }}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary inline-flex items-center gap-2 px-5 py-1.5 text-sm font-semibold rounded-xl cursor-pointer"
                                    onClick={handleSendReply}
                                    disabled={
                                        sending ||
                                        (isComposerEmpty(replyText) &&
                                            pendingFiles.length === 0)
                                    }
                                >
                                    <Icon value="sendHorizontal" size={15} />
                                    <span>
                                        {sending
                                            ? t("tasks.comments.sending")
                                            : t("tasks.comments.send")}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </Collapse>

                {/* Messages List (in reverse chronological order) */}
                <div className="space-y-4 mb-6">
                    {!messages ||
                    messages.filter(
                        (message: any) =>
                            commentFilter === "all" ||
                            (message.comment_type || "general") ===
                                commentFilter
                    ).length === 0 ? (
                        <p className="text-center text-sm text-slate-500 italic py-6">
                            {t("tasks.comments.empty")}
                        </p>
                    ) : (
                        messages
                            .filter(
                                (message: any) =>
                                    commentFilter === "all" ||
                                    (message.comment_type || "general") ===
                                        commentFilter
                            )
                            .map((msg: any, idx: number) => {
                                const isMsgAuthor = Boolean(
                                    user?.id &&
                                        Number(msg.user_id || msg.user?.id) ===
                                            Number(user.id)
                                );

                                return (
                                    <div
                                        key={msg.id || idx}
                                        className="space-y-3"
                                        id={`msg-${msg.id}`}
                                    >
                                        {/* Comment Box with hover toolbar */}
                                        <div className="message-box group relative bg-white dark:bg-zinc-900 border border-transparent hover:border-primary/50 dark:hover:border-primary/60 rounded-2xl p-4 transition-all">
                                            {/* Header: Left avatar + right (name + date) */}
                                            <div className="flex items-start sm:items-center gap-2.5 mb-2.5">
                                                <div className="avatar mt-0.5 sm:mt-0">
                                                    {msg.user?.avatar ? (
                                                        <img
                                                            src={
                                                                msg.user.avatar
                                                            }
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        getInitials(
                                                            msg.user?.name
                                                        )
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center justify-between gap-x-2 sm:gap-x-3 gap-y-1 flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                                            {msg.user?.name ||
                                                                t(
                                                                    "tasks.comments.unknown_user"
                                                                )}
                                                        </span>
                                                        {msg.is_internal && (
                                                            <Badge
                                                                variant="warning"
                                                                value={t(
                                                                    "tasks.comments.internal"
                                                                )}
                                                                size="sm"
                                                            />
                                                        )}
                                                        {msg.replies &&
                                                            msg.replies.length >
                                                                0 && (
                                                                <span className="px-1.5 py-0.2 rounded-full bg-slate-100 dark:bg-zinc-800 text-[10px] font-bold text-slate-600 dark:text-zinc-300">
                                                                    {
                                                                        msg
                                                                            .replies
                                                                            .length
                                                                    }
                                                                </span>
                                                            )}
                                                    </div>

                                                    {/* Date */}
                                                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-start sm:justify-end">
                                                        <span className="text-xs text-slate-400 dark:text-zinc-500 font-medium">
                                                            {date(
                                                                msg.created_at,
                                                                true
                                                            )}
                                                        </span>
                                                        {isMessageEdited(
                                                            msg.created_at,
                                                            msg.updated_at
                                                        ) && (
                                                            <span className="text-[11px] text-slate-400 dark:text-zinc-500 font-normal">
                                                                (
                                                                {t(
                                                                    "tasks.comments.edited"
                                                                )}
                                                                :{" "}
                                                                {date(
                                                                    msg.updated_at,
                                                                    true
                                                                )}
                                                                )
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Body or Inline Edit */}
                                            {editingMessageId === msg.id ? (
                                                <div className="mt-2 space-y-2">
                                                    <Textarea
                                                        value={editingContent}
                                                        onChange={(e) =>
                                                            setEditingContent(
                                                                e.target.value
                                                            )
                                                        }
                                                        rows={3}
                                                        autoGrow
                                                        richText
                                                        bare
                                                        rounded
                                                    />
                                                    <div className="flex items-center justify-center gap-2 pt-1">
                                                        <button
                                                            type="button"
                                                            className="btn-secondary px-3.5 py-1 text-xs font-semibold rounded-lg cursor-pointer"
                                                            onClick={
                                                                handleCancelEdit
                                                            }
                                                        >
                                                            {t("common.cancel")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-primary inline-flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-lg cursor-pointer"
                                                            onClick={() =>
                                                                handleSaveEdit(
                                                                    msg.id
                                                                )
                                                            }
                                                            disabled={
                                                                editSaving ||
                                                                isComposerEmpty(
                                                                    editingContent
                                                                )
                                                            }
                                                        >
                                                            <Icon
                                                                value="check"
                                                                size={13}
                                                            />
                                                            <span>
                                                                {editSaving
                                                                    ? t(
                                                                          "common.saving"
                                                                      )
                                                                    : t(
                                                                          "common.save"
                                                                      )}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className="richtext-content text-sm text-slate-800 dark:text-zinc-200 leading-relaxed pl-0.5"
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            msg.content || "",
                                                    }}
                                                    onClick={handleContentClick}
                                                />
                                            )}

                                            {/* Attachments if any */}
                                            {msg.attachments &&
                                                msg.attachments.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-3 pt-2.5 border-t border-slate-100 dark:border-zinc-800">
                                                        {msg.attachments.map(
                                                            (
                                                                att: any,
                                                                aIdx: number
                                                            ) => (
                                                                <div
                                                                    key={aIdx}
                                                                    className="flex items-center gap-1.5 text-xs bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-lg px-2.5 py-1"
                                                                >
                                                                    <Icon
                                                                        value="paperclip"
                                                                        size={
                                                                            13
                                                                        }
                                                                        className="text-slate-400"
                                                                    />
                                                                    <span className="truncate max-w-[160px]">
                                                                        {
                                                                            att.name
                                                                        }
                                                                    </span>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                )}

                                            {/* Action Buttons Centered over bottom border (hidden when editing or replying) */}
                                            {editingMessageId !== msg.id &&
                                                replyingToId !== msg.id && (
                                                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 flex items-center gap-1.5 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 shadow-xs rounded-full px-2 py-0.5 z-20">
                                                        {/* Reply */}
                                                        <Tooltip
                                                            title={t(
                                                                "tasks.comments.reply"
                                                            )}
                                                            placement="top"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    if (
                                                                        replyingToId ===
                                                                        msg.id
                                                                    ) {
                                                                        setReplyingToId(
                                                                            null
                                                                        );
                                                                        setThreadReplyText(
                                                                            ""
                                                                        );
                                                                    } else {
                                                                        setReplyingToId(
                                                                            msg.id
                                                                        );
                                                                        setThreadReplyText(
                                                                            ""
                                                                        );
                                                                    }
                                                                }}
                                                                className="p-1 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                aria-label={t(
                                                                    "tasks.comments.reply"
                                                                )}
                                                            >
                                                                <Icon
                                                                    value="reply"
                                                                    size={15}
                                                                />
                                                            </button>
                                                        </Tooltip>

                                                        {/* Quote */}
                                                        <Tooltip
                                                            title={t(
                                                                "tasks.comments.quote"
                                                            )}
                                                            placement="top"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleQuoteMessage(
                                                                        msg
                                                                    )
                                                                }
                                                                className="p-1 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                aria-label={t(
                                                                    "tasks.comments.quote"
                                                                )}
                                                            >
                                                                <Icon
                                                                    value="quote"
                                                                    size={15}
                                                                />
                                                            </button>
                                                        </Tooltip>

                                                        {/* Copy */}
                                                        <Tooltip
                                                            title={
                                                                copiedMsgId ===
                                                                msg.id
                                                                    ? t(
                                                                          "tasks.comments.copied"
                                                                      )
                                                                    : t(
                                                                          "tasks.comments.copy"
                                                                      )
                                                            }
                                                            placement="top"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleCopyContent(
                                                                        msg.content,
                                                                        msg.id
                                                                    )
                                                                }
                                                                className="p-1 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                aria-label={
                                                                    copiedMsgId ===
                                                                    msg.id
                                                                        ? t(
                                                                              "tasks.comments.copied"
                                                                          )
                                                                        : t(
                                                                              "tasks.comments.copy"
                                                                          )
                                                                }
                                                            >
                                                                <Icon
                                                                    value={
                                                                        copiedMsgId ===
                                                                        msg.id
                                                                            ? "check"
                                                                            : "copy"
                                                                    }
                                                                    size={15}
                                                                    className={
                                                                        copiedMsgId ===
                                                                        msg.id
                                                                            ? "text-emerald-500 dark:text-emerald-400"
                                                                            : ""
                                                                    }
                                                                />
                                                            </button>
                                                        </Tooltip>

                                                        {/* Edit (only if current user is author) */}
                                                        {isMsgAuthor && (
                                                            <Tooltip
                                                                title={t(
                                                                    "common.edit"
                                                                )}
                                                                placement="top"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleStartEdit(
                                                                            msg.id,
                                                                            msg.content
                                                                        )
                                                                    }
                                                                    className="p-1 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                    aria-label={t(
                                                                        "common.edit"
                                                                    )}
                                                                >
                                                                    <Icon
                                                                        value="pencil"
                                                                        size={
                                                                            15
                                                                        }
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                        )}

                                                        {/* Delete (only if current user is author) */}
                                                        {isMsgAuthor && (
                                                            <Tooltip
                                                                title={t(
                                                                    "tasks.comments.delete"
                                                                )}
                                                                placement="top"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setDeleteTargetMessageId(
                                                                            msg.id
                                                                        )
                                                                    }
                                                                    className="p-1 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
                                                                    aria-label={t(
                                                                        "tasks.comments.delete"
                                                                    )}
                                                                >
                                                                    <Icon
                                                                        value="trash"
                                                                        size={
                                                                            15
                                                                        }
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                )}
                                        </div>

                                        {/* Replies / Thread (indented below parent box) */}
                                        {msg.replies &&
                                            msg.replies.length > 0 && (
                                                <div className="ml-6 sm:ml-8 space-y-2.5 border-l-2 border-slate-200 dark:border-zinc-800 pl-3 sm:pl-4">
                                                    {msg.replies.map(
                                                        (
                                                            reply: any,
                                                            rIdx: number
                                                        ) => {
                                                            const isReplyAuthor =
                                                                Boolean(
                                                                    user?.id &&
                                                                        Number(
                                                                            reply.user_id ||
                                                                                reply
                                                                                    .user
                                                                                    ?.id
                                                                        ) ===
                                                                            Number(
                                                                                user.id
                                                                            )
                                                                );

                                                            return (
                                                                <div
                                                                    key={
                                                                        reply.id ||
                                                                        rIdx
                                                                    }
                                                                    id={`msg-${reply.id}`}
                                                                    className="message-box group relative bg-white dark:bg-zinc-900 border border-slate-200/80 dark:border-zinc-800 rounded-xl p-3 shadow-2xs"
                                                                >
                                                                    {/* Header: Left avatar + right (user + date) */}
                                                                    <div className="flex items-start sm:items-center gap-2 mb-1.5">
                                                                        <div className="avatar w-6 h-6 rounded-full bg-slate-200 dark:bg-zinc-700 flex items-center justify-center shrink-0 text-[10px] font-bold text-slate-700 dark:text-zinc-200 uppercase overflow-hidden mt-0.5 sm:mt-0">
                                                                            {reply
                                                                                .user
                                                                                ?.avatar ? (
                                                                                <img
                                                                                    src={
                                                                                        reply
                                                                                            .user
                                                                                            .avatar
                                                                                    }
                                                                                    alt=""
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            ) : (
                                                                                getInitials(
                                                                                    reply
                                                                                        .user
                                                                                        ?.name
                                                                                )
                                                                            )}
                                                                        </div>
                                                                        <div className="flex flex-wrap items-center justify-between gap-x-1.5 sm:gap-x-2 gap-y-0.5 flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                                                                    {reply
                                                                                        .user
                                                                                        ?.name ||
                                                                                        t(
                                                                                            "tasks.comments.unknown_user"
                                                                                        )}
                                                                                </span>
                                                                            </div>
                                                                            {/* Date */}
                                                                            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-start sm:justify-end">
                                                                                <span className="text-[11px] text-slate-400 dark:text-zinc-500 font-medium">
                                                                                    {date(
                                                                                        reply.created_at,
                                                                                        true
                                                                                    )}
                                                                                </span>
                                                                                {isMessageEdited(
                                                                                    reply.created_at,
                                                                                    reply.updated_at
                                                                                ) && (
                                                                                    <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-normal">
                                                                                        (
                                                                                        {t(
                                                                                            "tasks.comments.edited"
                                                                                        )}

                                                                                        :{" "}
                                                                                        {date(
                                                                                            reply.updated_at,
                                                                                            true
                                                                                        )}

                                                                                        )
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Body or Inline Edit */}
                                                                    {editingMessageId ===
                                                                    reply.id ? (
                                                                        <div className="mt-2 space-y-2">
                                                                            <div className="border border-slate-200 dark:border-zinc-700 rounded-xl overflow-hidden">
                                                                                <Textarea
                                                                                    value={
                                                                                        editingContent
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        setEditingContent(
                                                                                            e
                                                                                                .target
                                                                                                .value
                                                                                        )
                                                                                    }
                                                                                    rows={
                                                                                        2
                                                                                    }
                                                                                    autoGrow
                                                                                    richText
                                                                                    bare
                                                                                    rounded
                                                                                />
                                                                            </div>
                                                                            <div className="flex items-center justify-center gap-2 pt-1">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn-secondary px-3 py-0.5 text-xs font-semibold rounded-lg cursor-pointer"
                                                                                    onClick={
                                                                                        handleCancelEdit
                                                                                    }
                                                                                >
                                                                                    {t(
                                                                                        "common.cancel"
                                                                                    )}
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn-primary inline-flex items-center gap-1.5 px-3 py-0.5 text-xs font-semibold rounded-lg cursor-pointer"
                                                                                    onClick={() =>
                                                                                        handleSaveEdit(
                                                                                            reply.id
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        editSaving ||
                                                                                        isComposerEmpty(
                                                                                            editingContent
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <Icon
                                                                                        value="check"
                                                                                        size={
                                                                                            12
                                                                                        }
                                                                                    />
                                                                                    <span>
                                                                                        {editSaving
                                                                                            ? t(
                                                                                                  "common.saving"
                                                                                              )
                                                                                            : t(
                                                                                                  "common.save"
                                                                                              )}
                                                                                    </span>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div
                                                                            className="richtext-content text-xs text-slate-800 dark:text-zinc-200 leading-relaxed pl-1"
                                                                            dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                    reply.content ||
                                                                                    "",
                                                                            }}
                                                                            onClick={
                                                                                handleContentClick
                                                                            }
                                                                        />
                                                                    )}

                                                                    {/* Action Buttons Centered over bottom border (hidden when editing or replying) */}
                                                                    {editingMessageId !==
                                                                        reply.id &&
                                                                        replyingToId !==
                                                                            msg.id && (
                                                                            <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 flex items-center gap-1 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 shadow-xs rounded-full px-2 py-0.5 z-20">
                                                                                {/* Quote */}
                                                                                <Tooltip
                                                                                    title={t(
                                                                                        "tasks.comments.quote"
                                                                                    )}
                                                                                    placement="top"
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleQuoteMessage(
                                                                                                reply,
                                                                                                msg.id
                                                                                            )
                                                                                        }
                                                                                        className="p-0.5 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                                        aria-label={t(
                                                                                            "tasks.comments.quote"
                                                                                        )}
                                                                                    >
                                                                                        <Icon
                                                                                            value="quote"
                                                                                            size={
                                                                                                13
                                                                                            }
                                                                                        />
                                                                                    </button>
                                                                                </Tooltip>

                                                                                {/* Edit (only if current user is author) */}
                                                                                {isReplyAuthor && (
                                                                                    <Tooltip
                                                                                        title={t(
                                                                                            "common.edit"
                                                                                        )}
                                                                                        placement="top"
                                                                                    >
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                handleStartEdit(
                                                                                                    reply.id,
                                                                                                    reply.content
                                                                                                )
                                                                                            }
                                                                                            className="p-0.5 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                                            aria-label={t(
                                                                                                "common.edit"
                                                                                            )}
                                                                                        >
                                                                                            <Icon
                                                                                                value="pencil"
                                                                                                size={
                                                                                                    13
                                                                                                }
                                                                                            />
                                                                                        </button>
                                                                                    </Tooltip>
                                                                                )}

                                                                                {/* Delete (only if current user is author) */}
                                                                                {isReplyAuthor && (
                                                                                    <Tooltip
                                                                                        title={t(
                                                                                            "tasks.comments.delete"
                                                                                        )}
                                                                                        placement="top"
                                                                                    >
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                setDeleteTargetMessageId(
                                                                                                    reply.id
                                                                                                )
                                                                                            }
                                                                                            className="p-0.5 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
                                                                                            aria-label={t(
                                                                                                "tasks.comments.delete"
                                                                                            )}
                                                                                        >
                                                                                            <Icon
                                                                                                value="trash"
                                                                                                size={
                                                                                                    13
                                                                                                }
                                                                                            />
                                                                                        </button>
                                                                                    </Tooltip>
                                                                                )}

                                                                                {/* Copy */}
                                                                                <Tooltip
                                                                                    title={
                                                                                        copiedMsgId ===
                                                                                        reply.id
                                                                                            ? t(
                                                                                                  "tasks.comments.copied"
                                                                                              )
                                                                                            : t(
                                                                                                  "tasks.comments.copy"
                                                                                              )
                                                                                    }
                                                                                    placement="top"
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() =>
                                                                                            handleCopyContent(
                                                                                                reply.content,
                                                                                                reply.id
                                                                                            )
                                                                                        }
                                                                                        className="p-0.5 cursor-pointer transition-transform hover:scale-110 text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary"
                                                                                        aria-label={
                                                                                            copiedMsgId ===
                                                                                            reply.id
                                                                                                ? t(
                                                                                                      "tasks.comments.copied"
                                                                                                  )
                                                                                                : t(
                                                                                                      "tasks.comments.copy"
                                                                                                  )
                                                                                        }
                                                                                    >
                                                                                        <Icon
                                                                                            value={
                                                                                                copiedMsgId ===
                                                                                                reply.id
                                                                                                    ? "check"
                                                                                                    : "copy"
                                                                                            }
                                                                                            size={
                                                                                                13
                                                                                            }
                                                                                            className={
                                                                                                copiedMsgId ===
                                                                                                reply.id
                                                                                                    ? "text-emerald-500 dark:text-emerald-400"
                                                                                                    : ""
                                                                                            }
                                                                                        />
                                                                                    </button>
                                                                                </Tooltip>
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}

                                        {/* Inline reply box for this thread with smooth Collapse accordion animation */}
                                        <Collapse
                                            in={replyingToId === msg.id}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <div className="ml-6 sm:ml-8 border-l-2 border-primary/50 pl-3 sm:pl-4 pt-1">
                                                <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl p-3 shadow-xs">
                                                    <Textarea
                                                        value={threadReplyText}
                                                        onChange={(e) =>
                                                            setThreadReplyText(
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={t(
                                                            "tasks.comments.reply_placeholder"
                                                        )}
                                                        rows={2}
                                                        autoGrow
                                                        richText
                                                        bare
                                                    />
                                                    <div className="flex items-center justify-center gap-3 mt-3 pt-2 border-t border-slate-100 dark:border-zinc-800">
                                                        <button
                                                            type="button"
                                                            className="btn-secondary px-4 py-1 text-xs font-semibold rounded-lg cursor-pointer"
                                                            onClick={() => {
                                                                setReplyingToId(
                                                                    null
                                                                );
                                                                setThreadReplyText(
                                                                    ""
                                                                );
                                                            }}
                                                        >
                                                            {t("common.cancel")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-primary inline-flex items-center gap-1.5 px-4 py-1 text-xs font-semibold rounded-lg cursor-pointer"
                                                            onClick={() =>
                                                                handleSendThreadReply(
                                                                    msg.id
                                                                )
                                                            }
                                                            disabled={
                                                                threadSending ||
                                                                isComposerEmpty(
                                                                    threadReplyText
                                                                )
                                                            }
                                                        >
                                                            <Icon
                                                                value="sendHorizontal"
                                                                size={13}
                                                            />
                                                            <span>
                                                                {threadSending
                                                                    ? t(
                                                                          "tasks.comments.sending"
                                                                      )
                                                                    : t(
                                                                          "tasks.comments.reply"
                                                                      )}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })
                    )}
                </div>
            </div>

            <ChangeTaskStatusModal
                isOpen={statusTarget !== null}
                task={statusTarget}
                onClose={() => setStatusTarget(null)}
                onSuccess={(updated) => {
                    setStatusTarget(null);
                    setTask(updated);
                    if (onTaskUpdate) {
                        onTaskUpdate(updated);
                    }
                }}
            />

            <ChangeTaskPriorityModal
                isOpen={priorityTarget !== null}
                task={priorityTarget}
                onClose={() => setPriorityTarget(null)}
                onSuccess={(updated) => {
                    setPriorityTarget(null);
                    setTask(updated);
                    if (onTaskUpdate) {
                        onTaskUpdate(updated);
                    }
                }}
            />

            <ChangeTaskAssigneeModal
                isOpen={assigneeTarget !== null}
                task={assigneeTarget}
                onClose={() => setAssigneeTarget(null)}
                onSuccess={(updated) => {
                    setAssigneeTarget(null);
                    setTask(updated);
                    if (onTaskUpdate) {
                        onTaskUpdate(updated);
                    }
                }}
            />

            {!onEdit && (
                <NewTaskModal
                    isOpen={isEditModalOpen}
                    task={task}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={(updated) => {
                        setIsEditModalOpen(false);
                        setTask(updated);
                        if (onTaskUpdate) {
                            onTaskUpdate(updated);
                        }
                    }}
                    statuses={statuses}
                />
            )}

            <Modal
                isOpen={deleteTargetMessageId !== null}
                onClose={() => setDeleteTargetMessageId(null)}
                title={t("tasks.comments.delete_title")}
                maxWidth="sm"
                onSubmit={handleConfirmDeleteMessage}
                submitLabel={t("tasks.comments.delete")}
                submitVariant="danger"
                isSubmitting={isDeletingMessage}
            >
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                    {t("tasks.comments.delete_desc")}
                </p>
            </Modal>
        </div>
    );
};
